import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../SupabaseClient.tsx";
import "./LandingPage.css";

declare global {
  interface Window {
    handleSignInWithGoogle: (response: any) => Promise<void>;
    google: any;
  }
}

const LandingPage: React.FC = () => {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [isSignUp, setIsSignUp] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [signInError, setSignInError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    // Load Google Identity Services script
    const loadGoogleScript = () => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => {
        if (window.google && buttonRef.current) {
          window.google.accounts.id.initialize({
            client_id: "770499561610-bn7l337pafvj7uf68pn76hhaguvclt2j.apps.googleusercontent.com",
            callback: window.handleSignInWithGoogle,
          });

          window.google.accounts.id.renderButton(buttonRef.current, {
            type: "standard",
            shape: "rectangular",
            theme: "outline",
            text: "signin_with",
            size: "large",
            logo_alignment: "left",
            width: 280,
          });
        }
      };
      document.body.appendChild(script);
    };

    loadGoogleScript();

    return () => {
      // Cleanup if needed
      const scripts = document.querySelectorAll('script[src="https://accounts.google.com/gsi/client"]');
      scripts.forEach(script => script.remove());
    };
  }, []);

  window.handleSignInWithGoogle = async function (response) {
    const { error } = await supabase.auth.signInWithIdToken({
      provider: "google",
      token: response.credential,
    });

    if (error) {
      console.error("Error signing in with Google:", error);
      return;
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError("Email is required");
      return false;
    } else if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = (password: string): boolean => {
    // Password must be at least 8 characters and include uppercase, lowercase, number, and symbol
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    if (!password) {
      setPasswordError("Password is required");
      return false;
    } else if (!passwordRegex.test(password)) {
      setPasswordError(
        "Password must be at least 8 characters and include uppercase, lowercase, number, and symbol"
      );
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignInError("");

    if (!validateEmail(email)) return;
    if (isSignUp) {
      if (!validatePassword(password)) return;
      if (password !== confirmPassword) {
        setPasswordError("Passwords do not match");
        return;
      }
    }

    setLoading(true);

    try {
      if (isSignUp) {
        // Sign up
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin,
          },
        });

        if (error) throw error;
      } else {
        // Sign in
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      setSignInError(error.message || "An error occurred during authentication");
    } finally {
      setLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setIsSignUp(!isSignUp);
    setEmailError("");
    setPasswordError("");
    setSignInError("");
  };

  useEffect(() => {
    // Load Google Identity Services script
    const loadGoogleScript = () => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => {
        if (window.google && buttonRef.current) {
          window.google.accounts.id.initialize({
            client_id: "770499561610-bn7l337pafvj7uf68pn76hhaguvclt2j.apps.googleusercontent.com",
            callback: window.handleSignInWithGoogle,
          });

          window.google.accounts.id.renderButton(buttonRef.current, {
            type: "standard",
            shape: "rectangular",
            theme: "outline",
            text: "signin_with",
            size: "large",
            logo_alignment: "left",
            width: 280,
          });
        }
      };
      document.body.appendChild(script);
    };

    loadGoogleScript();

    return () => {
      // Cleanup if needed
      const scripts = document.querySelectorAll('script[src="https://accounts.google.com/gsi/client"]');
      scripts.forEach(script => script.remove());
    };
  }, []);

  return (
    <div className="landing-page">
      <div className="landing-container">
        <div className="landing-content">
          <div className="logo">
            <h1>Shoebox</h1>
          </div>

          <div className="welcome-section">
            <h2>Welcome to Shoebox</h2>
            <p>
              Store, track, and maximize your HSA receipts for better financial
              health
            </p>
          </div>

          <div className="sign-in-section">
            <h3>Sign in to continue</h3>

            {/* Email Authentication Form */}
            <form onSubmit={handleEmailSignIn} className="email-auth-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
                {emailError && <div className="error-message">{emailError}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
                {passwordError && <div className="error-message">{passwordError}</div>}
              </div>

              {isSignUp && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                  />
                </div>
              )}

              {signInError && (
                <div className="error-message signin-error">{signInError}</div>
              )}

              <button
                type="submit"
                className="auth-button"
                disabled={loading}
              >
                {loading ? 'Processing...' : isSignUp ? 'Sign Up' : 'Sign In'}
              </button>

              <div className="auth-toggle">
                <button
                  type="button"
                  onClick={toggleAuthMode}
                  className="toggle-button"
                >
                  {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
                </button>
              </div>
            </form>

            <div className="divider">
              <span>OR</span>
            </div>

            <div className="sign-in-options">
              <div ref={buttonRef} className="g_id_signin"></div>
            </div>
          </div>

          <div className="landing-footer">
            <p>
              By signing in, you agree to our{" "}
              <a href="/terms">Terms of Service</a> and{" "}
              <a href="/privacy-policy">Privacy Policy</a>
            </p>
          </div>
        </div>

        <div className="landing-illustration">
          <div className="feature-list">
            <div className="feature-item">
              <h4>Track All Receipts</h4>
              <p>
                Store and organize all your HSA eligible receipts in one place
              </p>
            </div>
            <div className="feature-item">
              <h4>Maximize Tax Benefits</h4>
              <p>See how your HSA investments can grow over time</p>
            </div>
            <div className="feature-item">
              <h4>Smart Receipt Processing</h4>
              <p>
                AI-powered receipt scanning identifies HSA eligible expenses
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden for safety */}
      <div
        id="g_id_onload"
        data-client_id="770499561610-bn7l337pafvj7uf68pn76hhaguvclt2j.apps.googleusercontent.com"
        data-context="signin"
        data-ux_mode="popup"
        data-callback="handleSignInWithGoogle"
        data-auto_select="true"
        data-close_on_tap_outside="false"
        data-itp_support="true"
      ></div>
    </div>
  );
};

export default LandingPage;
